import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { getApiErrorMessage } from "../../services/api";
import ExcelIcon from "../../assets/excel-icon.png";
import BackIcon from "../../assets/back-icon.svg";

const EventTickets = () => {
    const { id } = useParams();
    const navitate = useNavigate();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({
        totalPages: 0,
        totalItems: 0,
        currentPage: 1,
        totalCompletedStatus: 0,
        totalPendingStatus: 0,
        totalValidatedEntries: 0,
    });

    const [filters, setFilters] = useState({
        buyer_email: "",
        discount_code: "",
        payment_status: "",
        created_from: "",
        created_to: "",
        sort_by: "",
        sort_dir: "desc",
        page: 1,
        per_page: 10,
    });

    const [debouncedEmail, setDebouncedEmail] = useState("");
    const [debouncedDiscountCode, setDebouncedDiscountCode] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedEmail(filters.buyer_email);
        }, 500);
        return () => clearTimeout(timer);
    }, [filters.buyer_email]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDiscountCode(filters.discount_code);
        }, 500);
        return () => clearTimeout(timer);
    }, [filters.discount_code]);

    useEffect(() => {
        setFilters((prev) =>
            prev.page !== 1 ? { ...prev, page: 1 } : prev
        );
    }, [debouncedEmail, debouncedDiscountCode]);

    const getTicketsByEvent = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                sort_by: filters.sort_by,
                sort_dir: filters.sort_dir,
                page: filters.page,
                per_page: filters.per_page,
            };

            if (debouncedEmail) {
                params.buyer_email = debouncedEmail;
            }

            if (debouncedDiscountCode) {
                params.discount_code = debouncedDiscountCode;
            }

            if (filters.payment_status) {
                params.payment_status = filters.payment_status;
            }

            if (filters.created_from) {
                params.created_from = filters.created_from;
            }

            if (filters.created_to) {
                params.created_to = filters.created_to;
            }

            const res = await api.get(`/admin/tickets/${id}`, {
                params,
            });

            setTickets(res.data.data || res.data);
            setMeta({
                totalPages: res.data.totalPages || 1,
                totalItems: res.data.totalItems || (res.data.data ? res.data.data.length : res.data.length),
                currentPage: res.data.currentPage || 1,
                totalCompletedStatus: res.data.totalCompletedStatus || 0,
                totalPendingStatus: res.data.totalPendingStatus || 0,
                totalValidatedEntries: res.data.totalValidatedEntries || 0,
                totalRevenue: res.data.totalRevenue,
                totalExpiredStatus: res.data.totalExpiredStatus,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        id,
        debouncedEmail,
        debouncedDiscountCode,
        filters.payment_status,
        filters.created_from,
        filters.created_to,
        filters.sort_by,
        filters.sort_dir,
        filters.page,
        filters.per_page,
    ]);

    useEffect(() => {
        getTicketsByEvent();
    }, [getTicketsByEvent]);

    const downloadExcel = async (eventId) => {
        try {
            const response = await api.get(`/admin/downloadTicketsExcel/${eventId}`, {
                params: {
                    payment_status: 'completed',
                },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `tickets_${eventId}.xlsx`);

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error al descargar el archivo Excel:', error);
            if (!error.response || error.response.status >= 500) {
                alert(getApiErrorMessage(error));
            } else if (error.response.data instanceof Blob) {
                try {
                    const responseText = await error.response.data.text();
                    const errorJson = JSON.parse(responseText);
                    alert(`Error del servidor: ${errorJson.message || 'No se pudo generar el Excel.'}`);
                } catch {
                    alert('Ocurrio un error inesperado en el servidor.');
                }
            } else {
                alert(getApiErrorMessage(error, 'Error de conexion o permisos insuficientes.'));
            }
        }
    };


    return (
        <div className="md:col-span-2">
            <button className="my-4 px-2 py-1 text-white bg-indigo-600 rounded-xl hover:bg-indigo-300 flex gap-1" onClick={() => navitate('/admin/dashboard')}><img src={BackIcon} width={16} height={16} alt="Volver" />Volver</button>
            <div className="mb-8 border-b border-gray-200 pb-5">
                <div className="md:flex md:items-center md:justify-between">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                            Panel de ventas
                        </h2>
                        <p className="mt-2 text-sm text-gray-500">
                            Estado de tickets y cobros.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex gap-4 flex-col">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-4 bg-gray-50 p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex flex-col rounded-xl bg-white p-5 shadow-sm border border-gray-100">
                        <span className="text-sm font-medium text-gray-500">
                            Cantidad de ventas
                        </span>
                        <span className="mt-2 text-3xl font-bold text-gray-900 tracking-tight">
                            {meta.totalItems}
                        </span>
                    </div>

                    <div className="flex flex-col p-5 rounded-xl shadow-sm border border-green-100 bg-green-50">
                        <span className="text-sm font-medium text-green-700">
                            Pagos exitosos
                        </span>
                        <span className="mt-2 text-3xl font-bold text-green-600 tracking-tight">
                            {meta.totalCompletedStatus}
                        </span>
                    </div>

                    <div className="flex flex-col p-5 rounded-xl shadow-sm border border-amber-100 bg-amber-50">
                        <span className="text-sm font-medium text-amber-700">
                            Pagos pendientes
                        </span>
                        <span className="mt-2 text-3xl font-bold text-amber-600 tracking-tight">
                            {meta.totalPendingStatus}
                        </span>
                    </div>

                    {meta?.totalExpiredStatus !== undefined && (
                        <div className="flex flex-col p-5 rounded-xl shadow-sm border border-gray-100 bg-gray-50">
                            <span className="text-sm font-medium text-gray-700">
                                Expirados
                            </span>
                            <span className="mt-2 text-3xl font-bold text-gray-600 tracking-tight">
                                {meta.totalExpiredStatus}
                            </span>
                        </div>
                    )}

                    <div className="flex flex-col p-5 rounded-xl shadow-sm border border-emerald-100 bg-emerald-50 sm:col-span-2">
                        <span className="text-sm font-medium text-emerald-700">
                            Entradas validadas
                        </span>
                        <span className="mt-2 text-3xl font-bold text-emerald-600 tracking-tight">
                            {meta.totalValidatedEntries ?? 0}
                        </span>
                    </div>

                    {meta?.totalRevenue ? (
                        <div className="flex flex-col p-5 rounded-xl shadow-sm border border-blue-100 bg-blue-50 sm:col-span-2">
                            <span className="text-sm font-medium text-blue-700">
                                Total recaudado
                            </span>
                            <span className="mt-2 text-3xl font-bold text-blue-600 tracking-tight">
                                ${Intl.NumberFormat('es-AR').format(meta.totalRevenue)}
                            </span>
                        </div>
                    ) : undefined}
                </div>

                <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-gray-200">
                    <input
                        type="text"
                        placeholder="Buscar por email..."
                        value={filters.buyer_email}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                buyer_email: e.target.value,
                            }))
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
                    />

                    <input
                        type="text"
                        placeholder="Cod. descuento..."
                        value={filters.discount_code}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                discount_code: e.target.value,
                            }))
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
                    />

                    <select
                        value={filters.payment_status}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                payment_status: e.target.value,
                                page: 1,
                            }))
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                        <option value="">Todos los estados</option>
                        <option value="completed">Completado</option>
                        <option value="pending">Pendiente</option>
                        <option value="expired">Expirado</option>
                        <option value="requires_review">Requiere revision</option>
                    </select>

                    <button
                        onClick={() =>
                            setFilters({
                                buyer_email: "",
                                discount_code: "",
                                payment_status: "",
                                created_from: "",
                                created_to: "",
                                sort_by: "",
                                sort_dir: "desc",
                                page: 1,
                                per_page: 10,
                            })
                        }
                        className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm"
                    >
                        Limpiar filtros
                    </button>
                    <button className="flex gap-1 px-4 py-2 rounded-lg bg-indigo-400 hover:bg-indigo-300 text-white text-sm" onClick={() => downloadExcel(id)}>Descargar<img src={ExcelIcon} alt="Descargar" width={24} height={24} /></button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20 text-gray-400">
                        <span className="text-sm">Cargando entradas...</span>
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                        No hay registros para los filtros seleccionados.
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
                        <table className="w-full min-w-[1000px] border-collapse bg-white text-left text-sm text-gray-600 table-fixed">
                            <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="w-[6%] px-4 py-3.5">ID</th>
                                    <th className="w-[18%] px-4 py-3.5">Email</th>
                                    <th className="w-[7%] px-4 py-3.5 text-center">Cant.</th>
                                    <th className="w-[7%] px-4 py-3.5 text-center">Pagas</th>
                                    <th className="w-[7%] px-4 py-3.5 text-center">Gratis</th>
                                    <th className="w-[9%] px-4 py-3.5 text-center">Validadas</th>
                                    <th className="w-[11%] px-4 py-3.5">Cod. Desc.</th>
                                    <th className="w-[10%] px-4 py-3.5">Comp.</th>
                                    <th className="w-[12%] px-4 py-3.5 text-center">Estado</th>
                                    <th className="w-[13%] px-4 py-3.5 text-right">Fecha</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                                {tickets.map((ticket) => (
                                    <tr key={ticket.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3.5 font-mono text-xs font-semibold text-gray-400">#{ticket.id}</td>
                                        <td className="px-4 py-3.5 font-medium text-gray-900 truncate">{ticket.buyer_email}</td>
                                        <td className="px-4 py-3.5 text-center font-mono font-medium text-gray-800">{ticket.quantity}</td>
                                        <td className="px-4 py-3.5 text-center font-mono text-gray-700">{ticket.paid_quantity ?? ticket.quantity}</td>
                                        <td className="px-4 py-3.5 text-center font-mono text-gray-700">{ticket.free_quantity ?? 0}</td>
                                        <td className="px-4 py-3.5 text-center">
                                            <span className={`font-mono font-semibold ${(ticket.attended_entries ?? 0) > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                                                {ticket.attended_entries ?? 0}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 truncate">
                                            {ticket.discount_code ? (
                                                <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs text-gray-700 border border-gray-200">
                                                    {ticket.discount_code}
                                                </span>
                                            ) : (
                                                <span className="text-gray-300 font-mono text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5 text-xs text-gray-600 truncate">{ticket.competitor_dni || '-'}</td>
                                        <td className="px-4 py-3.5 text-center">
                                            <span
                                                className={`inline-flex items-center justify-center w-24 py-1 text-[10px] font-bold rounded-md ${
                                                    ticket.payment_status === "completed"
                                                        ? "bg-green-50 text-green-700 border border-green-100"
                                                        : ticket.payment_status === "expired"
                                                        ? "bg-gray-50 text-gray-700 border border-gray-100"
                                                        : ticket.payment_status === "requires_review"
                                                        ? "bg-red-50 text-red-700 border border-red-100"
                                                        : "bg-amber-50 text-amber-700 border border-amber-100"
                                                }`}
                                            >
                                                {ticket.payment_status?.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 text-right font-mono text-xs text-gray-400 whitespace-nowrap">
                                            {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventTickets;
